import { useEffect } from 'react';
import { UseFormGetValues } from 'react-hook-form';

import { QuickQuoteResponse } from 'lib/api/getQuickQuote';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';
import { nl2br } from 'utils/getJoditEditorHelpers';

import {
  CarrierCostType,
  LTLQuote,
  QuickQuoteInputs,
  QuoteMode,
} from '../types';
import {
  fillDraftRate,
  formatFinalPriceInDraft,
  formatLTLDraftLocation,
  formatLTLDraftTemplate,
  getLTLSellRate,
  swapDraftRateInBody,
} from '../util';

/**
 * Hook that handles FTL draft response updates
 */
export const useFTLDraftResponse = (
  quoteMode: QuoteMode,
  quoteResponse: Maybe<QuickQuoteResponse>,
  finalFlatPrice: Maybe<number>,
  finalPriceFormat: CarrierCostType | 'Both',
  maxDistance: number,
  fuelEstimate: number,
  draftResponse: string,
  userEditedDraft: boolean,
  currentPriceString: string,
  setDraftResponse: (response: string) => void,
  setCurrentPriceString: (price: string) => void,
  formatAmount?: (amount: number, maxFractionDigits?: number) => string,
  isBillRateEditSessionActive = false
) => {
  useEffect(() => {
    if (quoteMode === QuoteMode.LTL) {
      return;
    }

    if (isBillRateEditSessionActive) {
      return;
    }

    const finalPrice = formatFinalPriceInDraft(
      finalFlatPrice,
      finalPriceFormat,
      maxDistance,
      fuelEstimate,
      formatAmount
    );

    const preRenderedBody = quoteResponse?.quoteReplyDraftTemplate?.body || '';

    // After manual edits, only swap the tracked auto-rate when it is still present
    // (user changed unrelated fields). Skip if the user manually changed the rate text.
    if (userEditedDraft) {
      const shouldOnlySwapRate =
        draftResponse &&
        currentPriceString &&
        draftResponse.includes(currentPriceString);

      if (shouldOnlySwapRate) {
        const finalDraft = swapDraftRateInBody(
          draftResponse,
          currentPriceString,
          finalPrice
        );
        setDraftResponse(nl2br(finalDraft));
      }

      setCurrentPriceString(finalPrice);
      return;
    }

    // The API pre-renders all lane fields server-side and leaves "{{rate}}" as the
    // only remaining token. Seed from the template once, then swap in-place so
    // unrelated draft content is not reset on subsequent price changes.
    const finalDraft = draftResponse
      ? swapDraftRateInBody(draftResponse, currentPriceString, finalPrice)
      : fillDraftRate(preRenderedBody, finalPrice);

    setDraftResponse(nl2br(finalDraft));
    setCurrentPriceString(finalPrice);
  }, [
    quoteResponse,
    finalFlatPrice,
    finalPriceFormat,
    quoteMode,
    maxDistance,
    fuelEstimate,
    draftResponse,
    userEditedDraft,
    currentPriceString,
    setDraftResponse,
    setCurrentPriceString,
    formatAmount,
    isBillRateEditSessionActive,
  ]);
};

/**
 * Hook that handles LTL draft response updates
 * LTL quotes have embedded pricing, so we use selectedLTLQuotes[].totalCost directly
 * Generates a paragraph for each selected quote using backend template
 */
export const useLTLDraftResponse = (
  quoteMode: QuoteMode,
  isLTLQuickQuoteEnabled: boolean,
  selectedLTLQuotes: LTLQuote[],
  ltlTemplateBody: Undef<string>,
  userEditedDraft: boolean,
  ltlExternalQuoteID: Maybe<string>,
  ltlExternalQuoteSource: Maybe<string>,
  isThreeGLTL: boolean,
  getValues: UseFormGetValues<QuickQuoteInputs>,
  setDraftResponse: (response: string) => void,
  setCurrentPriceString: (price: string) => void,
  hideTaiLimitedLiability = false
) => {
  useEffect(() => {
    if (
      !isLTLQuickQuoteEnabled ||
      quoteMode !== QuoteMode.LTL ||
      selectedLTLQuotes.length === 0
    ) {
      return;
    }

    const stops = getValues('stops');
    const pickupDate = getValues('pickupDate');
    const deliveryDate = getValues('deliveryDate');

    // Parse stops for display
    const pickupStop = stops[0];
    const deliveryStop = stops[stops.length - 1];

    const [primaryQuote] = selectedLTLQuotes;
    const pickupLocation = formatLTLDraftLocation(
      pickupStop,
      primaryQuote.pickupLocation ?? {},
      'Origin'
    );
    const deliveryLocation = formatLTLDraftLocation(
      deliveryStop,
      primaryQuote.deliveryLocation ?? {},
      'Destination'
    );

    // Use backend template or fallback to default
    const templateBody = ltlTemplateBody || FALLBACK_LTL_TEMPLATE;

    // Format the template with quote data
    const ltlDraftTemplate = formatLTLDraftTemplate(
      templateBody,
      pickupLocation,
      deliveryLocation,
      pickupDate ? new Date(pickupDate) : undefined,
      deliveryDate ? new Date(deliveryDate) : undefined,
      selectedLTLQuotes,
      ltlExternalQuoteID,
      ltlExternalQuoteSource,
      isThreeGLTL || ltlExternalQuoteSource?.toLowerCase() === TMS.Tai
        ? { rateFractionDigits: 2, hideTaiLimitedLiability }
        : { hideTaiLimitedLiability }
    );

    if (!userEditedDraft) {
      setDraftResponse(nl2br(ltlDraftTemplate));
    }

    // Track all prices for potential updates
    const allPrices = selectedLTLQuotes
      .map(
        (q) =>
          `$${getLTLSellRate(q).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`
      )
      .join(', ');

    setCurrentPriceString(allPrices);
  }, [
    quoteMode,
    isLTLQuickQuoteEnabled,
    selectedLTLQuotes,
    ltlTemplateBody,
    userEditedDraft,
    ltlExternalQuoteID,
    ltlExternalQuoteSource,
    isThreeGLTL,
    getValues,
    setDraftResponse,
    setCurrentPriceString,
    hideTaiLimitedLiability,
  ]);
};

// Fallback template if backend doesn't provide one
const FALLBACK_LTL_TEMPLATE = `<p>Hi,</p>
<p>Thank you for your LTL shipping inquiry.</p>
<p>Here {{#isMultipleQuotes}}are {{quoteCount}} quotes{{/isMultipleQuotes}}{{#isSingleQuote}}is our quote{{/isSingleQuote}} for your shipment from {{pickupLocation}} to {{deliveryLocation}}:</p>
{{#quotes}}<p><strong>Carrier:</strong> {{carrierName}}</p>{{#hasServiceLevel}}<p><strong>Service Level:</strong>{{serviceLevel}}</p>{{/hasServiceLevel}}{{#guaranteed}}<p><strong>Service Type:</strong> Guaranteed</p>{{/guaranteed}}<p><strong>Transit Time:</strong> {{transitDaysText}}</p><p><strong>Rate:</strong> {{rate}}</p>{{#hasInsurance}}<p><strong>Insurance:</strong> {{coverageType}}{{#coverageAmount}} - {{coverageAmount}}{{/coverageAmount}}</p>{{/hasInsurance}}</p><br>{{/quotes}}
{{#hasPickupDate}}<p><strong>Pickup Date:</strong> {{pickupDate}}</p>{{/hasPickupDate}}{{#hasDeliveryDate}}<p><strong>Delivery Date:</strong> {{deliveryDate}}</p>{{/hasDeliveryDate}}
{{#hasExternalQuoteID}}<p><strong>{{externalQuoteLabel}}:</strong> {{externalQuoteID}}</p>{{/hasExternalQuoteID}}
<p>Please let me know if you have any questions or would like to proceed with {{#isMultipleQuotes}}any of these quotes{{/isMultipleQuotes}}{{#isSingleQuote}}this quote{{/isSingleQuote}}.</p>
<p>Best regards</p>`;
